/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { LogoIcon } from './logo-icon';
import { SocialMediaIcons } from './SocialMediaIcons';

interface FooterProps {
    onNavigate?: (view: string, hash?: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  return (
    <footer className="bg-stone-900 text-stone-400 pt-24 pb-12 border-t border-stone-800">
      <div className="container mx-auto px-6">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-20">
              
              {/* Brand Column */}
              <div className="lg:col-span-5 flex flex-col gap-8">
                  <div className="flex items-center gap-3">
                       <div className="w-10 h-10 rounded-full bg-stone-900 border-2 border-stone-700 flex items-center justify-center shadow-md overflow-hidden">
                           <img src="/assets/logo.svg" alt="Logo" className="w-full h-full object-cover p-2" />
                       </div>
                       <div className="flex flex-col justify-center">
                            <div className="font-serif font-bold text-2xl text-white tracking-tight leading-none">
                              <span className="font-light">THE</span> FOUNDRY
                            </div>
                            <div className="text-[10px] font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-500 to-amber-600 tracking-[0.2em] uppercase mt-1">
                              Launch • Connect • Scale
                            </div>
                       </div>
                  </div>
                  <p className="text-sm leading-relaxed text-stone-500 max-w-sm">
                      The premier decentralized launchpad for the next generation of digital assets on Solana & Sui. 
                      Built for high-velocity token generation, instant liquidity, and AI-powered market intelligence.
                  </p>
                  
                  <div className="flex items-center gap-6 mt-2">
                      <div className="text-xs font-bold text-stone-600 uppercase tracking-widest">Supported Networks</div>
                      <div className="flex gap-3">
                          <img src="https://cryptologos.cc/logos/solana-sol-logo.png" className="w-6 h-6 grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100" title="Solana" />
                          <img src="https://cryptologos.cc/logos/sui-sui-logo.png" className="w-6 h-6 grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100" title="Sui" />
                      </div>
                  </div>
              </div>

              {/* Navigation Grid */}
              <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-10">
                  <div>
                      <h4 className="text-white font-bold text-sm mb-6 uppercase tracking-wider">Platform</h4>
                      <ul className="space-y-4 text-sm font-medium">
                          <li><button onClick={() => onNavigate?.('ALL_MARKETS')} className="hover:text-nobel-gold transition-colors">Market Terminal</button></li>
                          <li><button onClick={() => onNavigate?.('LAUNCH')} className="hover:text-nobel-gold transition-colors">Launch Token</button></li>
                          <li><button onClick={() => onNavigate?.('AI_DESK')} className="hover:text-nobel-gold transition-colors">AI Trading Desk</button></li>
                          <li><button onClick={() => onNavigate?.('RANKING')} className="hover:text-nobel-gold transition-colors">Leaderboard</button></li>
                      </ul>
                  </div>
                  <div>
                      <h4 className="text-white font-bold text-sm mb-6 uppercase tracking-wider">Resources</h4>
                      <ul className="space-y-4 text-sm font-medium">
                          <li><button onClick={() => onNavigate?.('API_ACCESS')} className="hover:text-nobel-gold transition-colors">API & Docs</button></li>
                          <li><button onClick={() => onNavigate?.('FEES')} className="hover:text-nobel-gold transition-colors">Fees Structure</button></li>
                          <li><button onClick={() => onNavigate?.('HELP')} className="hover:text-nobel-gold transition-colors">Help Center</button></li>
                          <li><a href="#" className="hover:text-nobel-gold transition-colors">Brand Assets</a></li>
                      </ul>
                  </div>
                  <div>
                      <h4 className="text-white font-bold text-sm mb-6 uppercase tracking-wider">Legal</h4>
                      <ul className="space-y-4 text-sm font-medium">
                          <li><button onClick={() => onNavigate?.('LEGAL')} className="hover:text-nobel-gold transition-colors">Terms of Service</button></li>
                          <li><button onClick={() => onNavigate?.('LEGAL')} className="hover:text-nobel-gold transition-colors">Privacy Policy</button></li>
                          <li><a href="#" className="hover:text-nobel-gold transition-colors">Risk Disclosure</a></li>
                      </ul>
                  </div>
              </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="pt-8 border-t border-stone-800 flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="text-xs text-stone-600 font-mono">
                  <p>© 2025 THE FOUNDRY. All rights reserved.</p>
              </div>
              <SocialMediaIcons className="flex gap-4" />
          </div>
      </div>
    </footer>
  );
};